<script setup>
import { computed, nextTick, onBeforeUnmount, ref } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ContactAPI from 'dashboard/api/contacts';
import { useStore } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { CONTACTS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { isAceleresGroupContact } from 'dashboard/helper/aceleresGroupContact';
import { useContactMergeI18n } from '../useContactMergeI18n';
import MergeContactCard from './MergeContactCard.vue';

const props = defineProps({
  selectedContact: { type: Object, required: true },
});
const emit = defineEmits(['merged']);
const { t } = useContactMergeI18n();
const store = useStore();
const dialog = ref(null);
const searchInput = ref(null);
const isOpen = ref(false);
const query = ref('');
const results = ref([]);
const resultDetails = ref({});
const searching = ref(false);
const loading = ref(false);
const merging = ref(false);
const error = ref('');
const current = ref(null);
const chosen = ref(null);
const inverted = ref(false);
const snapshots = ref({});
let searchTimer;
let searchController;
let generation = 0;
const step = computed(() => (chosen.value ? 2 : 1));
const primary = computed(() => (inverted.value ? chosen.value : current.value));
const removed = computed(() => (inverted.value ? current.value : chosen.value));
const phone = contact =>
  (contact.phone_number || contact.phoneNumber || '').replace(/\D/g, '');
const duplicateReason = contact => {
  if (phone(contact) && phone(contact) === phone(props.selectedContact))
    return 'SAME_PHONE';
  if (
    contact.email &&
    contact.email.toLowerCase() === props.selectedContact.email?.toLowerCase()
  )
    return 'SAME_EMAIL';
  return '';
};
const sortedResults = computed(() =>
  [...results.value].sort(
    (a, b) => Number(!!duplicateReason(b)) - Number(!!duplicateReason(a))
  )
);
const channelNames = computed(() => ({
  whatsapp: t('MERGE_DIALOG.WHATSAPP'),
  email: t('MERGE_DIALOG.EMAIL'),
  instagram: t('MERGE_DIALOG.INSTAGRAM'),
  messenger: t('MERGE_DIALOG.MESSENGER'),
  outro: t('MERGE_DIALOG.OTHER'),
}));
const resultMetadata = contact => {
  const details = resultDetails.value[contact.id];
  const parts = [contact.phone_number || contact.email || contact.identifier];
  if (details) {
    parts.push(
      details.channels
        .map(channel => channelNames.value[channel] || channelNames.value.outro)
        .join(', ')
    );
    parts.push(
      t('MERGE_DIALOG.CONVERSATIONS', details.conversations, {
        count: details.conversations,
      })
    );
  }
  if (duplicateReason(contact))
    parts.push(t(`MERGE_DIALOG.${duplicateReason(contact)}`));
  return parts.filter(Boolean).join(' · ');
};

// Limit concurrent reads and reuse details within this modal's search session.
const loadResultDetails = async (contacts, controller) => {
  const missing = contacts.filter(contact => !resultDetails.value[contact.id]);
  if (controller.signal.aborted || !missing.length) return;
  await Promise.all(
    missing.slice(0, 3).map(async contact => {
      try {
        const { data } = await ContactAPI.identities(contact.id);
        if (controller.signal.aborted) return;
        resultDetails.value[contact.id] = {
          channels: [
            ...new Set(data.payload.map(identity => identity.channel)),
          ],
          conversations: data.payload.reduce(
            (count, identity) => count + (identity.conversations_count || 0),
            0
          ),
        };
      } catch {
        // A failed optional summary must not prevent selecting a contact.
      }
    })
  );
  await loadResultDetails(missing.slice(3), controller);
};

const summary = contact => snapshots.value[contact?.id]?.summary;

const loadContact = async id => {
  const [details, identities] = await Promise.all([
    ContactAPI.show(id),
    ContactAPI.identities(id),
  ]);
  const entries = identities.data.payload || [];
  return {
    contact: details.data.payload,
    summary: {
      connections: entries.reduce(
        (count, item) => count + (item.connections?.length || 0),
        0
      ),
      conversations: entries.reduce(
        (count, item) => count + (item.conversations_count || 0),
        0
      ),
    },
  };
};

const search = async () => {
  searchController?.abort();
  const controller = new AbortController();
  searchController = controller;
  results.value = [];
  error.value = '';
  if (!query.value.trim()) {
    searching.value = false;
    return;
  }
  searching.value = true;
  try {
    const { data } = await ContactAPI.search(
      encodeURIComponent(query.value.trim()),
      1,
      'name',
      '',
      { signal: controller.signal }
    );
    if (controller.signal.aborted || !isOpen.value) return;
    results.value = data.payload.filter(
      contact =>
        String(contact.id) !== String(props.selectedContact.id) &&
        !isAceleresGroupContact(contact)
    );
    loadResultDetails(results.value, controller);
  } catch {
    if (!controller.signal.aborted && isOpen.value)
      error.value = t('MERGE_DIALOG.LOAD_ERROR');
  } finally {
    if (searchController === controller) searching.value = false;
  }
};
const onSearch = () => {
  clearTimeout(searchTimer);
  searchController?.abort();
  results.value = [];
  searching.value = !!query.value.trim();
  searchTimer = setTimeout(search, 300);
};
const reset = () => {
  isOpen.value = false;
  generation += 1;
  clearTimeout(searchTimer);
  searchController?.abort();
  chosen.value = null;
  inverted.value = false;
  results.value = [];
  error.value = '';
  loading.value = false;
  searching.value = false;
};
const open = async () => {
  if (merging.value) return;
  reset();
  isOpen.value = true;
  const run = generation;
  current.value = props.selectedContact;
  snapshots.value = {};
  resultDetails.value = {};
  query.value =
    props.selectedContact.name ||
    phone(props.selectedContact) ||
    props.selectedContact.email ||
    '';
  dialog.value.open();
  await nextTick();
  searchInput.value?.focus();
  search();
  try {
    const snapshot = await loadContact(props.selectedContact.id);
    if (!isOpen.value || run !== generation) return;
    snapshots.value[snapshot.contact.id] = snapshot;
    current.value = snapshot.contact;
  } catch {
    // Selection reloads both records before enabling the irreversible action.
  }
};
const selectContact = async contact => {
  if (loading.value) return;
  const run = generation;
  loading.value = true;
  error.value = '';
  try {
    const [base, candidate] = await Promise.all([
      loadContact(props.selectedContact.id),
      loadContact(contact.id),
    ]);
    if (!isOpen.value || run !== generation) return;
    snapshots.value = {
      [base.contact.id]: base,
      [candidate.contact.id]: candidate,
    };
    current.value = base.contact;
    chosen.value = candidate.contact;
    inverted.value = false;
  } catch {
    if (isOpen.value && run === generation)
      error.value = t('MERGE_DIALOG.LOAD_ERROR');
  } finally {
    if (run === generation) loading.value = false;
  }
};
const goBack = async () => {
  chosen.value = null;
  inverted.value = false;
  error.value = '';
  await nextTick();
  searchInput.value?.focus();
};
const merge = async () => {
  if (!chosen.value || loading.value || merging.value) return;
  merging.value = true;
  error.value = '';
  try {
    await store.dispatch('contacts/merge', {
      parentId: primary.value.id,
      childId: removed.value.id,
    });
    useTrack(CONTACTS_EVENTS.MERGED_CONTACTS);
    useAlert(t('MERGE_DIALOG.SUCCESS'));
    dialog.value.close();
    emit('merged');
  } catch {
    error.value = t('MERGE_DIALOG.ERROR');
  } finally {
    merging.value = false;
  }
};
onBeforeUnmount(reset);
defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialog"
    :aria-label="t('MERGE_DIALOG.TITLE')"
    :width="step === 1 ? '2xl' : '3xl'"
    overflow-y-auto
    :backdrop-blur="false"
    backdrop-class="bg-[#101828]/40"
    panel-class="!gap-0 !rounded-[20px] !bg-white !p-0 !backdrop-blur-none"
    :dialog-class="[
      'max-h-[90dvh] !rounded-[20px] max-sm:!w-[calc(100%_-_32px)]',
      step === 1 ? '!max-w-[680px]' : '!max-w-[820px]',
    ]"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="reset"
    @confirm="merge"
  >
    <header
      class="flex items-center gap-[12px] px-[24px] pb-[16px] pt-[24px] max-sm:px-[16px]"
    >
      <span
        class="flex size-[40px] shrink-0 items-center justify-center rounded-[12px] bg-[#eef8f1] text-[#078c38]"
        ><span class="i-lucide-git-merge size-[20px]"
      /></span>
      <div class="min-w-0 flex-1">
        <h2 class="m-0 text-[18px] leading-[24px] font-semibold text-[#111827]">
          {{ t('MERGE_DIALOG.TITLE') }}
        </h2>
        <p class="m-0 text-[14px] leading-[20px] text-[#667085]">
          {{ t('MERGE_DIALOG.DESCRIPTION') }}
        </p>
      </div>
      <span
        class="shrink-0 text-[12px] leading-[18px] text-[#8a95a5] max-sm:hidden"
        >{{ t('MERGE_DIALOG.STEP', { step }) }}</span
      >
      <Button
        type="button"
        icon="i-lucide-x"
        :aria-label="t('MERGE_DIALOG.CLOSE')"
        slate
        faded
        size="sm"
        class="!size-[36px] !rounded-[12px]"
        :disabled="merging"
        @click="dialog.close()"
      />
    </header>
    <div class="px-[24px] pb-[20px] max-sm:px-[16px]">
      <template v-if="step === 1">
        <MergeContactCard
          :contact="current || selectedContact"
          :summary="summary(current)"
        />
        <label
          for="merge-contact-search"
          class="mb-[8px] mt-[16px] block text-[14px] leading-[20px] font-semibold text-[#111827]"
          >{{ t('MERGE_DIALOG.SEARCH_LABEL') }}</label
        >
        <div
          class="flex items-center gap-[12px] rounded-[12px] border border-[#10a344] px-[16px] focus-within:ring-1 focus-within:ring-[#10a344]"
        >
          <span class="i-lucide-search size-[16px] shrink-0 text-[#667085]" />
          <input
            id="merge-contact-search"
            ref="searchInput"
            v-model="query"
            type="search"
            autocomplete="off"
            :placeholder="t('MERGE_DIALOG.SEARCH_PLACEHOLDER')"
            class="!m-0 !h-[44px] !min-w-0 !border-0 !bg-transparent !p-0 !text-[14px] leading-[20px] !text-[#111827] !shadow-none !outline-none"
            @input="onSearch"
          />
        </div>
        <div
          class="mt-[16px] flex max-h-[320px] flex-col gap-[8px] overflow-y-auto"
          aria-live="polite"
        >
          <p
            v-if="searching"
            class="m-0 py-[12px] text-[14px] leading-[20px] text-[#667085]"
          >
            {{ t('MERGE_DIALOG.SEARCHING') }}
          </p>
          <p
            v-else-if="!sortedResults.length && !error"
            class="m-0 py-[12px] text-[14px] leading-[20px] text-[#667085]"
          >
            {{ t('MERGE_DIALOG.EMPTY') }}
          </p>
          <div
            v-for="contact in sortedResults"
            :key="contact.id"
            class="flex items-center gap-[12px] rounded-[12px] border p-[12px]"
            :class="
              duplicateReason(contact)
                ? 'border-[#d1efdd] bg-[#f3faf5]'
                : 'border-[#edf0f3] bg-white'
            "
          >
            <Avatar
              :name="contact.name || ''"
              :src="contact.thumbnail || ''"
              :size="36"
              rounded-full
            />
            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center gap-[8px]">
                <span
                  class="text-[14px] leading-[20px] font-semibold text-[#111827]"
                  >{{ contact.name }}</span
                >
                <span
                  v-if="duplicateReason(contact)"
                  class="rounded-full bg-[#10a344] px-2 py-0.5 text-[10px] font-bold text-white"
                  >{{ t('MERGE_DIALOG.DUPLICATE') }}</span
                >
              </div>
              <p
                class="m-0 break-words text-[12px] leading-[18px] text-[#667085]"
              >
                {{ resultMetadata(contact) }}
              </p>
            </div>
            <Button
              type="button"
              :label="t('MERGE_DIALOG.SELECT')"
              size="sm"
              :disabled="loading"
              class="shrink-0 !h-[34px] !rounded-[8px] !px-[14px] !text-[13px] !font-semibold"
              :class="
                duplicateReason(contact)
                  ? '!bg-[#10a344] !text-white hover:!bg-[#078c38]'
                  : '!border !border-solid !border-[#d8dee8] !bg-white !text-[#111827]'
              "
              @click="selectContact(contact)"
            />
          </div>
        </div>
      </template>
      <template v-else>
        <div
          class="grid grid-cols-[minmax(0,1fr)_36px_minmax(0,1fr)] items-center gap-[12px] max-sm:grid-cols-1"
        >
          <MergeContactCard
            :contact="removed"
            :summary="summary(removed)"
            role="REMOVED"
          />
          <div
            class="flex flex-col items-center gap-[8px] max-sm:flex-row max-sm:justify-center"
          >
            <span
              class="flex size-[36px] items-center justify-center rounded-full bg-[#111827] text-white"
              ><span class="i-lucide-arrow-right size-[16px] max-sm:rotate-90"
            /></span>
            <Button
              type="button"
              icon="i-lucide-arrow-right-left"
              :aria-label="t('MERGE_DIALOG.SWAP')"
              :title="t('MERGE_DIALOG.SWAP')"
              size="sm"
              slate
              faded
              class="!size-[36px] !rounded-full !border !border-solid !border-[#edf0f3] !bg-white"
              :disabled="merging"
              @click="inverted = !inverted"
            />
          </div>
          <MergeContactCard
            :contact="primary"
            :summary="summary(primary)"
            role="PRIMARY"
          />
        </div>
        <section class="mt-[16px] rounded-[16px] bg-[#f6f7f7] p-[16px]">
          <h3
            class="mb-[8px] text-[11px] font-semibold tracking-wider text-[#8a95a5]"
          >
            {{ t('MERGE_DIALOG.SUMMARY') }}
          </h3>
          <ul
            class="m-0 flex list-none flex-col gap-[8px] p-0 text-[14px] leading-[20px] text-[#667085]"
          >
            <li class="flex gap-[8px]">
              <span
                class="i-lucide-check mt-[2px] size-[16px] shrink-0 text-[#10a344]"
              />{{
                t('MERGE_DIALOG.MOVED', {
                  name: primary.name,
                  conversations: t(
                    'MERGE_DIALOG.CONVERSATIONS',
                    summary(removed).conversations,
                    { count: summary(removed).conversations }
                  ),
                  connections: t(
                    'MERGE_DIALOG.CONNECTIONS',
                    summary(removed).connections,
                    { count: summary(removed).connections }
                  ),
                })
              }}
            </li>
            <li
              v-for="key in ['FIELDS', 'IDENTITIES', 'OWNER_RULES']"
              :key="key"
              class="flex gap-[8px]"
            >
              <span
                class="i-lucide-check mt-[2px] size-[16px] shrink-0 text-[#10a344]"
              />{{ t(`MERGE_DIALOG.${key}`, { name: primary.name }) }}
            </li>
          </ul>
        </section>
        <p class="mb-0 mt-[16px] text-[12px] leading-[18px] text-[#dc2626]">
          {{ t('MERGE_DIALOG.WARNING', { name: removed.name }) }}
        </p>
      </template>
      <p
        v-if="loading"
        class="mb-0 mt-[12px] flex items-center gap-[8px] text-[14px] leading-[20px] text-[#667085]"
        role="status"
      >
        <Spinner class="size-[16px]" />{{ t('MERGE_DIALOG.LOADING') }}
      </p>
      <p
        v-if="error"
        role="alert"
        class="mb-0 mt-[12px] text-[14px] leading-[20px] text-[#dc2626]"
      >
        {{ error }}
      </p>
    </div>
    <template #footer>
      <footer
        class="flex flex-wrap items-center justify-end gap-[8px] border-t border-[#f0f2f5] px-[24px] py-[16px] max-sm:px-[16px]"
      >
        <Button
          v-if="step === 2"
          type="button"
          :label="t('MERGE_DIALOG.BACK')"
          icon="i-lucide-arrow-left"
          slate
          ghost
          class="mr-auto !text-[14px] !font-semibold !text-[#475467]"
          :disabled="merging"
          @click="goBack"
        />
        <Button
          type="button"
          :label="t('MERGE_DIALOG.CANCEL')"
          slate
          faded
          class="!h-[42px] !rounded-[12px] !border !border-solid !border-[#d8dee8] !bg-white !px-[18px] !text-[14px] !text-[#111827]"
          :disabled="merging"
          @click="dialog.close()"
        />
        <Button
          v-if="step === 2"
          type="button"
          :label="t('MERGE_DIALOG.CONFIRM')"
          :is-loading="merging"
          :disabled="merging || loading"
          class="!h-[42px] !rounded-[12px] !bg-[#10a344] !px-[20px] !text-[14px] !font-semibold !text-white hover:!bg-[#078c38]"
          @click="merge"
        />
      </footer>
    </template>
  </Dialog>
</template>
